import { useState, useCallback } from 'react'

export interface UseFormStateConfig {
  onSubmit: (data: unknown) => Promise<void>
  onSuccess?: () => void
}

export interface UseFormStateResult {
  isSubmitting: boolean
  error: string | null
  clearError: () => void
  handleSubmit: (data: unknown) => Promise<void>
}

/**
 * Wraps the try/catch/finally + isSubmitting + error state that every form
 * repeats. Does NOT manage field state — that varies too much per form.
 *
 * Usage:
 *   const { isSubmitting, error, handleSubmit } = useFormState({
 *     onSubmit: async (data) => { await createHabit(data as HabitFormData) },
 *     onSuccess: () => { onClose(); resetFields() },
 *   })
 */
export function useFormState(config: UseFormStateConfig): UseFormStateResult {
  const { onSubmit, onSuccess } = config

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => setError(null), [])

  const handleSubmit = useCallback(
    async (data: unknown) => {
      setError(null)
      setIsSubmitting(true)
      try {
        await onSubmit(data)
        onSuccess?.()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setIsSubmitting(false)
      }
    },
    [onSubmit, onSuccess]
  )

  return { isSubmitting, error, clearError, handleSubmit }
}
